import { CalendarDays, Clock, Save } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../../../api/axios.js";
import { useAuth } from "../../../AuthContext";

function Tablework() {
  const {
    user: contextUser,
    subscriptionActive,
    loading: authLoading,
  } = useAuth(); // ✅ استخدام الاشتراك من الـ context

  const [startHour, setStartHour] = useState(8);
  const [startMinute, setStartMinute] = useState(0);
  const [endHour, setEndHour] = useState(17);
  const [endMinute, setEndMinute] = useState(0);

  const [workingDays, setWorkingDays] = useState({
    Monday: false,
    Tuesday: false,
    Wednesday: false,
    Thursday: false,
    Friday: false,
    Saturday: false,
    Sunday: false,
  });

  const daysFrench = {
    Monday: "Lundi",
    Tuesday: "Mardi",
    Wednesday: "Mercredi",
    Thursday: "Jeudi",
    Friday: "Vendredi",
    Saturday: "Samedi",
    Sunday: "Dimanche",
  };

  // 🔹 جلب الجدول
  useEffect(() => {
    if (!contextUser) return; // إذا المستخدم لم يُحمل بعد
    api
      .get("/work-schedule")
      .then((res) => {
        const data = res.data.data;
        if (!data) return;

        const [sh, sm] = data.start_time.split(":");
        const [eh, em] = data.end_time.split(":");

        setStartHour(Number(sh));
        setStartMinute(Number(sm));
        setEndHour(Number(eh));
        setEndMinute(Number(em));

        const daysObj = {
          Monday: false,
          Tuesday: false,
          Wednesday: false,
          Thursday: false,
          Friday: false,
          Saturday: false,
          Sunday: false,
        };

        data.days.forEach((d) => {
          daysObj[d.day] = Boolean(d.is_working);
        });

        setWorkingDays(daysObj);
      })
      .catch((err) => {
        console.error("❌ Failed to load schedule", err);
      });
  }, [contextUser]); // 🔹 إعادة التحميل عند تغير المستخدم

  const toggleDay = (day) => {
    if (!subscriptionActive) return; // الآن يعتمد على حالة الاشتراك من AuthContext
    setWorkingDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!subscriptionActive) {
      alert("❌ يجب تفعيل الاشتراك أولاً");
      return;
    }

    const payload = {
      start_time: `${String(startHour).padStart(2, "0")}:${String(
        startMinute,
      ).padStart(2, "0")}`,
      end_time: `${String(endHour).padStart(2, "0")}:${String(
        endMinute,
      ).padStart(2, "0")}`,
      working_days: Object.keys(workingDays).map((day) => ({
        day,
        is_working: workingDays[day],
      })),
    };

    try {
      await api.post("/work-schedule/save", payload);
      alert("✅ تم حفظ جدول العمل بنجاح");
    } catch (err) {
      console.error(err);
      alert("❌ فشل حفظ جدول العمل");
    }
  };

  // 🔹 عرض تحميل إذا لم يُحمل المستخدم بعد
  if (authLoading) return <p>...جاري التحميل</p>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white w-full max-w-md rounded-2xl shadow-lg p-6 space-y-6"
      >
        {!subscriptionActive && (
          <div className="bg-yellow-100 text-yellow-800 text-sm p-3 rounded-lg">
            ⚠️ هذه الميزة تتطلب اشتراكًا نشطًا عبر التطبيق
          </div>
        )}

        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800">
          <CalendarDays size={20} />
          جدول العمل
        </h3>

        {/* وقت البداية */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-600">
            <Clock size={16} /> بداية العمل
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              max="23"
              disabled={!subscriptionActive}
              className="w-1/2 rounded-lg border px-3 py-2 disabled:bg-gray-100"
              value={startHour}
              onChange={(e) => setStartHour(Number(e.target.value))}
            />
            <input
              type="number"
              min="0"
              max="59"
              disabled={!subscriptionActive}
              className="w-1/2 rounded-lg border px-3 py-2 disabled:bg-gray-100"
              value={startMinute}
              onChange={(e) => setStartMinute(Number(e.target.value))}
            />
          </div>
        </div>

        {/* وقت النهاية */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-600">
            <Clock size={16} /> نهاية العمل
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              max="23"
              disabled={!subscriptionActive}
              className="w-1/2 rounded-lg border px-3 py-2 disabled:bg-gray-100"
              value={endHour}
              onChange={(e) => setEndHour(Number(e.target.value))}
            />
            <input
              type="number"
              min="0"
              max="59"
              disabled={!subscriptionActive}
              className="w-1/2 rounded-lg border px-3 py-2 disabled:bg-gray-100"
              value={endMinute}
              onChange={(e) => setEndMinute(Number(e.target.value))}
            />
          </div>
        </div>

        {/* أيام العمل */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-600">أيام العمل</p>
          <div className="grid grid-cols-2 gap-2">
            {Object.keys(workingDays).map((day) => (
              <label
                key={day}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  disabled={!subscriptionActive}
                  checked={workingDays[day]}
                  onChange={() => toggleDay(day)}
                />
                {daysFrench[day]}
              </label>
            ))}
          </div>
        </div>

        {/* زر الحفظ */}
        <button
          type="submit"
          disabled={!subscriptionActive}
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl transition
            ${
              subscriptionActive
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-gray-400 cursor-not-allowed text-gray-700"
            }`}
        >
          <Save size={16} />
          حفظ
        </button>
      </form>
    </div>
  );
}

export default Tablework;
